import { useState, useEffect } from "react";
import { tr } from "@/i18n";
import { ChevronDown, ChevronRight, Trash2, Search, AlertTriangle, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Eraser } from "lucide-react";
import { PanelHeader } from "@/components/settings/SettingsUI";
import { useAuth } from "@/auth/AuthContext";
import { getCleanupCandidates, previewCleanup, executeCleanup, previewFullWipe, executeFullWipe } from "@/lib/api";

const WIPE_ALLOWED_EMAIL = "nathan.fletemeyer@absinc.com";

const COLL_LABELS = {
  clients: "Customers", sites: "Sites", equipment: "Equipment",
  proposals: "Proposals", work_orders: "Work Orders", dispatches: "Dispatches",
  purchase_orders: "Purchase Orders", inspections: "Inspections",
  invoices: "Invoices", ar_notes: "AR Notes", leads: "Leads",
  parts_orders: "Parts Orders", timesheets: "Timesheets",
  dispatch_events: "Dispatch Events", tech_states: "Tech GPS States",
  communications: "Communications", jobs: "Jobs", parts: "Parts (legacy)",
  ar_reminder_log: "AR Reminder Log", email_log: "Email Log",
  sales_alerts: "Sales Alerts", proposal_followup_log: "Follow-up Log",
  purchasing_inbox: "Purchasing Inbox", inventory_log: "Inventory Log",
  geocoding_errors: "Geocoding Errors", login_attempts: "Login Attempts",
  password_reset_tokens: "Password Reset Tokens", miter_push_log: "Miter Push Log",
  miter_mock_inbox: "Miter Mock Inbox", gpt_ingest_quotas: "GPT Ingest Quotas",
  foundation_push_log: "Foundation Push Log", square_events_seen: "Square Webhook Events",
  users: "Users", technicians: "Technicians", account_managers: "Account Managers",
  project_managers: "Project Managers", settings: "Settings",
};

function FullWipeSection() {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const allowed = (user?.email || "").toLowerCase() === WIPE_ALLOWED_EMAIL || user?.is_bypass;
  if (!allowed) return null;

  const loadPreview = async () => {
    setBusy(true);
    try {
      const { data } = await previewFullWipe();
      setPreview(data);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runWipe = async () => {
    setBusy(true);
    try {
      const { data } = await executeFullWipe("WIPE ALL TEST DATA");
      toast.success(`Wiped ${data.total} records — customers & settings kept`);
      setPreview(null);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-red-300 rounded-lg p-4 bg-red-50/40 space-y-3" data-testid="full-wipe-section">
      <div className="flex items-start gap-2 text-xs text-red-800">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          <b>Wipe ALL test data</b> — deletes every proposal, work order, invoice, dispatch,
          PO, parts order, timesheet, lead, inspection and log. <b>Keeps</b> all customers,
          sites, equipment, users, technicians, every settings/catalog collection, number
          counters, and Foundation/Miter sync data. Permanent.
        </span>
      </div>
      {!preview ? (
        <Button variant="outline" size="sm" className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-100" onClick={loadPreview} disabled={busy} data-testid="full-wipe-preview-btn">
          {busy ? "Loading…" : "Preview full wipe"}
        </Button>
      ) : (
        <div className="space-y-3" data-testid="full-wipe-preview-result">
          <div className="text-sm font-semibold text-red-700">
            This will permanently delete {preview.total} records:
          </div>
          {preview.total > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(preview.counts).map(([coll, n]) => (
                <div key={coll} className="flex justify-between px-2.5 py-1.5 bg-white border border-red-100 rounded text-xs">
                  <span className="text-slate-600">{COLL_LABELS[coll] || coll}</span>
                  <span className="font-bold text-red-600 tabular-nums">{n}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-500">Nothing to wipe — all transactional collections are already empty.</div>
          )}
          <div className="text-xs text-emerald-700">
            Kept untouched: {Object.entries(preview.kept || {}).map(([c, n]) => `${n} ${COLL_LABELS[c] || c}`).join(" · ")} + all catalogs & counters
          </div>
          <div className="flex items-center gap-2">
            <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder='Type WIPE ALL TEST DATA to confirm' className="h-8 text-xs max-w-xs" data-testid="full-wipe-confirm-input" />
            <Button variant="destructive" size="sm" className="h-8" disabled={confirmText !== "WIPE ALL TEST DATA" || busy || preview.total === 0} onClick={runWipe} data-testid="full-wipe-execute-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />{busy ? "Wiping…" : "Wipe permanently"}
            </Button>
            <Button variant="ghost" size="sm" className="h-8" onClick={() => setPreview(null)}>{tr("Cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DataCleanupPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selClients, setSelClients] = useState(new Set());
  const [selLeads, setSelLeads] = useState(new Set());
  const [includeOrphans, setIncludeOrphans] = useState(false);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [executing, setExecuting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await getCleanupCandidates();
      setData(d);
    } catch {
      toast.error("Failed to load cleanup candidates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (!data) load(); }, []); // eslint-disable-line

  const selectionBody = () => ({
    client_ids: [...selClients],
    lead_ids: [...selLeads],
    include_orphans: includeOrphans,
  });

  const runPreview = async () => {
    try {
      const { data: p } = await previewCleanup(selectionBody());
      setPreview(p);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const runExecute = async () => {
    setExecuting(true);
    try {
      const { data: r } = await executeCleanup({ ...selectionBody(), confirm: "DELETE SELECTED" });
      toast.success(`Deleted ${r.total} records`);
      setPreview(null); setSelClients(new Set()); setSelLeads(new Set()); setConfirmText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setExecuting(false);
    }
  };

  const toggleSet = (set, setFn, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setFn(next);
  };

  const q = search.trim().toLowerCase();
  const customers = (data?.customers || []).filter(c => !q || (c.name || "").toLowerCase().includes(q));
  const leads = (data?.leads || []).filter(l => !q || `${l.name || ""} ${l.company || ""}`.toLowerCase().includes(q));
  const selCount = selClients.size + selLeads.size + (includeOrphans ? 1 : 0);
  const selectAllMatching = () => {
    setSelClients(new Set([...selClients, ...customers.map(c => c.id)]));
    setSelLeads(new Set([...selLeads, ...leads.map(l => l.id)]));
  };

  return (
    <div>
      <PanelHeader
        icon={Eraser}
        iconClass="text-rose-500"
        title="Go-Live Data Cleanup"
        hint="admin · destructive"
        testId="toggle-data-cleanup"
      />
      {true && (
        <div className="px-5 py-4 space-y-4" data-testid="data-cleanup-panel">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Selectively delete test customers, leads and everything attached to them (sites, proposals,
              work orders, dispatches, POs, invoices, inspections) while keeping real records.
              The Parts Counter customer is protected and never deletable. Deletions are permanent.
            </span>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder='Filter (e.g. "test")' className="h-8 pl-8 text-xs" data-testid="cleanup-search" />
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={selectAllMatching} data-testid="cleanup-select-matching">
              Select all shown
            </Button>
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setSelClients(new Set()); setSelLeads(new Set()); }}>
              {tr("Clear")}
            </Button>
            <Button variant="ghost" size="sm" className="h-8" onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600">
                Customers ({customers.length}) — cascades to all their records
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {customers.map(c => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer" data-testid={`cleanup-client-${c.id}`}>
                    <Checkbox checked={selClients.has(c.id)} onCheckedChange={() => toggleSet(selClients, setSelClients, c.id)} />
                    <span className="text-xs font-medium text-slate-700 flex-1 truncate">
                      {c.name}{c.is_sandbox ? " 🧪" : ""}
                    </span>
                    <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap">
                      {c.work_orders} WO · {c.proposals} prop · {c.invoices} inv
                    </span>
                  </label>
                ))}
                {!customers.length && <div className="px-3 py-4 text-xs text-slate-400">No customers match</div>}
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600">Leads ({leads.length})</div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {leads.map(l => (
                  <label key={l.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer" data-testid={`cleanup-lead-${l.id}`}>
                    <Checkbox checked={selLeads.has(l.id)} onCheckedChange={() => toggleSet(selLeads, setSelLeads, l.id)} />
                    <span className="text-xs font-medium text-slate-700 flex-1 truncate">{l.name || l.company || l.id}</span>
                    <span className="text-[10px] text-slate-400">{l.status || ""}</span>
                  </label>
                ))}
                {!leads.length && <div className="px-3 py-4 text-xs text-slate-400">No leads match</div>}
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={includeOrphans} onCheckedChange={v => setIncludeOrphans(!!v)} data-testid="cleanup-orphans-toggle" />
            <span className="text-xs text-slate-600">
              Also delete <b>orphaned records</b> — work orders / proposals / invoices whose customer was already deleted
              {data?.orphans && ` (${data.orphans.work_orders} WO · ${data.orphans.proposals} prop · ${data.orphans.invoices} inv)`}
            </span>
          </label>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={runPreview} disabled={!selCount} data-testid="cleanup-preview-btn">
              Preview deletion ({selCount ? `${selClients.size} customers, ${selLeads.size} leads${includeOrphans ? ", + orphans" : ""}` : "nothing selected"})
            </Button>
          </div>

          <FullWipeSection />

          {preview && (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50/50 space-y-3" data-testid="cleanup-preview-result">
              <div className="text-sm font-semibold text-red-700">
                This will permanently delete {preview.total} records:
              </div>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(preview.counts).filter(([, n]) => n > 0).map(([coll, n]) => (
                  <div key={coll} className="flex justify-between px-2.5 py-1.5 bg-white border border-red-100 rounded text-xs">
                    <span className="text-slate-600">{COLL_LABELS[coll] || coll}</span>
                    <span className="font-bold text-red-600 tabular-nums">{n}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder='Type DELETE SELECTED to confirm' className="h-8 text-xs max-w-xs" data-testid="cleanup-confirm-input" />
                <Button variant="destructive" size="sm" className="h-8" disabled={confirmText !== "DELETE SELECTED" || executing} onClick={runExecute} data-testid="cleanup-execute-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />{executing ? "Deleting…" : "Delete permanently"}
                </Button>
                <Button variant="ghost" size="sm" className="h-8" onClick={() => setPreview(null)}>{tr("Cancel")}</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
